import { useCallback, useState } from "react";
import {
  Button,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/WarningRounded";
import { ToggleStyles } from "../../../../Settings/SettingsStyles";
import PopupData from "./PopupData";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { NAV_HEIGHT_PX, Z_INDEX } from "constants/constants";
import {
  AnimatedFloatStyles,
  FlightDebuggerStyles,
} from "./FlightDebuggerStyles";
import { animated } from "@react-spring/web";
import styled, { css } from "styled-components/macro";
import { mockMemoryLeakToCrashBrowserTab } from "utils/memoryUtils";
import {
  ControlsAnchor,
  useBindDragHandler,
  DEBUGGER_PADDING,
  DEBUGGER_HEADER_HEIGHT,
  useDebuggerState,
  useDebuggerPopupState,
  // braceAtom,
} from "./debuggerUtils";
import { getIsTouchDevice } from "utils/getIsTouchDevice";
import { FlagsEditor } from "./debuggerComponents/FlagsEditor";
import { CUSTOM_SCROLLBAR_CSS } from "App/CUSTOM_SCROLLBAR_CSS";
import { useForceMidgardBreakerOpen, useIsDarkMode } from "store/atoms";
import {
  darkModeCardBackgroundColor,
  darkModeDarkTextColor,
  darkModeTextColor,
} from "constants/colors";
import { FlightConfigEditor } from "./debuggerComponents/FlightConfigEditor";
import Settings from "@mui/icons-material/Settings";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import "jsoneditor-react/es/editor.min.css";
import { DebuggerHeader } from "./debuggerComponents/DebuggerHeader";
import { useMount } from "react-use";
import ControlGroupAskr from "./debuggerComponents/ControlGroups/ControlGroupAskr";
import ControlGroupAuth from "./debuggerComponents/ControlGroups/ControlGroupAuth";
import ControlGroupMidgard from "./debuggerComponents/ControlGroups/ControlGroupMidgard";
import ControlGroupLaunchDarkly from "./debuggerComponents/ControlGroups/ControlGroupLD";
import { useDataRequiredOnThisPage } from "utils/customHooks/useDataRequiredPerPage";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useAtom } from "jotai";
import { disableAdminModeAtom } from "components/Settings/SettingsPage";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";

const anchor = ControlsAnchor.TOP_LEFT;

const DEBUGGER_WIDTH = 417;

// tslint:disable-next-line: no-big-function
const FlightDebugger = () => {
  /** for touch devices, dragging doesn't work, so we'll pin it to the bottom and expand all the way up on click */
  const { innerWidth } = useWindowSize();
  const isTouchDevice = getIsTouchDevice() && innerWidth < 600;

  const [isDarkMode] = useIsDarkMode();
  const [{ collapsed }] = useDebuggerState();
  const { bindDragHandler, style } = useBindDragHandler({
    anchor,
    width: DEBUGGER_WIDTH,
    localStorageKey: "flight-debugger:position",
  });

  return (
    <FlightDebuggerStyles
      $width={DEBUGGER_WIDTH}
      data-component-name={"Flight Debugger"}
    >
      <DebuggerMouseLeaveHandler />
      <AnimatedFloatStyles
        data-width={DEBUGGER_WIDTH}
        data-anchor={anchor}
        $isDarkMode={isDarkMode}
        $isTouchDevice={isTouchDevice}
        style={{
          zIndex: Z_INDEX.L11 + 999,
          ...(isTouchDevice ? {} : style),
          ...(collapsed ? { height: "fit-content" } : {}),
        }}
      >
        <DebuggerHeader bindDragHandler={bindDragHandler} />
        {collapsed ? null : (
          <AnimatedItemsStyles
            $isDarkMode={isDarkMode}
            // hidden={collapsed}
            style={
              isTouchDevice
                ? {
                    maxHeight: `calc(100vh - ${
                      NAV_HEIGHT_PX + DEBUGGER_HEADER_HEIGHT + 24
                    }px)`,
                  }
                : undefined
            }
          >
            <DebuggerTitleRow />
            <AnimatedItemsContent />
          </AnimatedItemsStyles>
        )}
      </AnimatedFloatStyles>
    </FlightDebuggerStyles>
  );
};
function DebuggerMouseLeaveHandler() {
  const [, setDebuggerPopupState] = useDebuggerPopupState();
  useMount(() => {
    const handleMouseLeave = () => {
      setDebuggerPopupState(prev => {
        return prev.isPreview && prev.isPopupOpen
          ? {
              ...prev,
              isPopupOpen: false,
              popupData: null,
            }
          : prev;
      });
    };
    document
      .querySelector(`[data-component-name="Flight Debugger"]`)
      ?.addEventListener("mouseleave", handleMouseLeave);
    return () =>
      document
        .querySelector(`[data-component-name="Flight Debugger"]`)
        ?.removeEventListener("mouseleave", handleMouseLeave);
  });
  return null;
}
export function FlightDebuggerPopupDataContainer() {
  const [{ isPopupOpen }] = useDebuggerPopupState();
  return (
    <>
      {isPopupOpen ? (
        <ErrorBoundary
          small={true}
          overlay={true}
          id="FlightDebugger_PopupData"
        >
          <PopupData />
        </ErrorBoundary>
      ) : null}
    </>
  );
}
export default FlightDebugger;

const AnimatedItemsStyles = styled(animated.div)<{ $isDarkMode: boolean }>`
  background: white;
  padding: 16px 8px 16px 16px;
  overflow-y: auto;
  height: calc(100% - ${DEBUGGER_PADDING.y + 42}px);
  max-height: calc(100vh - ${DEBUGGER_PADDING.y * 2 + 42}px);
  ${CUSTOM_SCROLLBAR_CSS}
  ${({ $isDarkMode }) =>
    $isDarkMode
      ? css`
          background: ${darkModeCardBackgroundColor};
          color: ${darkModeTextColor};
          .darkText {
            color: ${darkModeTextColor};
          }
          .lightText {
            color: ${darkModeDarkTextColor};
          }
          .number {
            color: ${darkModeTextColor} !important;
          }
        `
      : ""}
`;

function CrashMenu() {
  const [forceMidgardBreakerOpen, setForceMidgardBreakerOpen] =
    useForceMidgardBreakerOpen();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        onTouchEnd={handleClick}
        variant="outlined"
        style={{ textTransform: "none" }}
        size="small"
        startIcon={<WarningIcon color={"secondary"} />}
      >
        Crash something
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            mockMemoryLeakToCrashBrowserTab();
            handleClose();
          }}
          onTouchEnd={() => {
            mockMemoryLeakToCrashBrowserTab();
            handleClose();
          }}
        >
          Crash the tab
        </MenuItem>
        {!forceMidgardBreakerOpen && (
          <MenuItem
            onClick={() => {
              setForceMidgardBreakerOpen(true);
              handleClose();
            }}
            onTouchEnd={() => {
              setForceMidgardBreakerOpen(true);
              handleClose();
            }}
          >
            Spam Midgard once (force breaker open)
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

function AnimatedItemsContent() {
  const dataRequiredOnThisPage = useDataRequiredOnThisPage();
  return (
    <>
      <div>
        <ErrorBoundary small={true} id="ControlGroup_Auth">
          <ControlGroupAuth dataRequiredOnThisPage={dataRequiredOnThisPage} />
        </ErrorBoundary>
      </div>
      <div>
        <ErrorBoundary small={true} id="ControlGroup_Askr">
          <ControlGroupAskr dataRequiredOnThisPage={dataRequiredOnThisPage} />
        </ErrorBoundary>
      </div>
      <div>
        <ErrorBoundary small={true} id="ControlGroup_Midgard">
          <ControlGroupMidgard
            dataRequiredOnThisPage={dataRequiredOnThisPage}
          />
        </ErrorBoundary>
      </div>
      <div>
        <ErrorBoundary small={true} id="ControlGroup_LaunchDarkly">
          <ControlGroupLaunchDarkly
            dataRequiredOnThisPage={dataRequiredOnThisPage}
          />
        </ErrorBoundary>
      </div>
    </>
  );
}

function DebuggerTitleRow() {
  const isAdminUser = useIsAdmin();
  const [, setDebuggerPopupState] = useDebuggerPopupState();

  const handleShowFlagsEditor = useCallback(
    isClick => {
      setDebuggerPopupState(prev =>
        // ignore mouseovers if popup is already open & not a preview
        !isClick && !prev.isPreview && prev.isPopupOpen
          ? prev
          : {
              ...prev,
              isPopupOpen: true,
              isPreview: isClick ? false : prev.isPreview,
              popupData: {
                networkName: "Feature Flags - these reset on page reload 👩‍🔬",
                data: null,
                viewStore: false,
                CustomComponent: FlagsEditor,
              },
            }
      );
    },
    [setDebuggerPopupState]
  );
  const handleShowFlightConfigEditor = useCallback(
    isClick => {
      setDebuggerPopupState(prev =>
        // ignore mouseovers if popup is already open & not a preview
        !isClick && !prev.isPreview && prev.isPopupOpen
          ? prev
          : {
              ...prev,
              isPopupOpen: true,
              isPreview: isClick ? false : prev.isPreview,
              popupData: {
                networkName:
                  "Flight Config - resets on page reload or switching customer 👩‍🔬",
                data: null,
                viewStore: false,
                CustomComponent: () => (
                  <ErrorBoundary
                    small={true}
                    overlay={true}
                    id="FlightConfigEditor"
                  >
                    <FlightConfigEditor />
                  </ErrorBoundary>
                ),
                popupId: "flightConfigEditor",
              },
            }
      );
    },
    [setDebuggerPopupState]
  );
  const handleShowStoreData = useCallback(
    isClick => {
      setDebuggerPopupState(
        (
          prev // ignore mouseovers if popup is already open & not a preview
        ) =>
          !isClick && !prev.isPreview && prev.isPopupOpen
            ? prev
            : {
                ...prev,
                isPopupOpen: true,
                isPreview: isClick ? false : prev.isPreview,
                popupData: {
                  networkName: "Global State Store",
                  data: null,
                  viewStore: true,
                },
              }
      );
    },
    [setDebuggerPopupState]
  );
  const [disableAdminMode, setDisableAdminMode] = useAtom(disableAdminModeAtom);
  const [isDarkMode] = useIsDarkMode();
  const onMouseLeave = useCallback(() => {
    setDebuggerPopupState(prev =>
      !prev.isPreview
        ? prev
        : {
            ...prev,
            popupData: null,
          }
    );
  }, [setDebuggerPopupState]);
  const { flightAdminMode } = useFlagsWithDefaults();
  return (
    <div className="titleRow">
      <div className="gridRows">
        <div className="gridColumns">
          <Button
            {...{ onMouseLeave, variant: "outlined", size: "small" }}
            onClick={() => {
              handleShowStoreData(true);
            }}
            onTouchEnd={() => {
              handleShowStoreData(true);
            }}
            onMouseEnter={() => {
              handleShowStoreData(false);
            }}
            startIcon={"👑"}
          >
            Store
          </Button>
          <Button
            {...{ onMouseLeave, variant: "outlined", size: "small" }}
            onClick={() => {
              handleShowFlightConfigEditor(true);
            }}
            onTouchEnd={() => {
              handleShowFlightConfigEditor(true);
            }}
            onMouseEnter={() => {
              handleShowFlightConfigEditor(false);
            }}
            startIcon={
              <Settings
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 4,
                  color: "lightslategrey",
                }}
              />
            }
          >
            Config
          </Button>
          {isAdminUser && (
            <Button
              {...{
                onMouseLeave,
                variant: "outlined",
                size: "small",
              }}
              onClick={() => {
                handleShowFlagsEditor(true);
              }}
              onTouchEnd={() => {
                handleShowFlagsEditor(true);
              }}
              onMouseEnter={() => {
                handleShowFlagsEditor(false);
              }}
              startIcon={"🚩"}
            >
              Flags
            </Button>
          )}
        </div>
        <div className="togglesRow">
          {flightAdminMode && (
            <ToggleStyles
              css={`
                label {
                  box-sizing: border-box;
                  padding: 0.5em 0 0;
                }
              `}
              className={disableAdminMode ? "checked" : ""}
              $isDarkMode={isDarkMode}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!disableAdminMode}
                    onChange={() => setDisableAdminMode(!disableAdminMode)}
                    style={disableAdminMode ? { opacity: 0.5 } : {}}
                  />
                }
                label={"Admin Mode"}
                labelPlacement="start"
              />
            </ToggleStyles>
          )}
          <CrashMenu />
        </div>
      </div>
    </div>
  );
}
